import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { BellIcon } from '@heroicons/react/24/outline';
import { useNotification } from '../../contexts/NotificationContext';

const NotificationDropdown = ({ onClose }) => {
  const { notifications, loading, markAsRead, markAllAsRead } = useNotification();
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.read) {
      await markAsRead(notification.id || notification._id);
    }
    onClose();
  };

  // Format date to relative time
  const formatDate = (date) => {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffInHours = Math.abs(now - notificationDate) / 36e5;

    if (diffInHours < 24) {
      return format(notificationDate, 'HH:mm', { locale: fr });
    } else if (diffInHours < 48) {
      return 'Hier';
    } else {
      return format(notificationDate, 'dd MMM', { locale: fr });
    }
  };

  return (
    <div
      ref={dropdownRef}
      className="origin-top-right absolute right-0 mt-2 w-80 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl ring-1 ring-black/5 focus:outline-none z-50"
    >
      <div role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
          <button
            onClick={handleMarkAllAsRead}
            className="text-xs font-medium text-primary-600 transition-colors hover:text-primary-800"
          >
            Tout marquer comme lu
          </button>
        </div>

        <div className="max-h-96 overflow-y-auto divide-y divide-gray-100">
          {loading ? (
            <div className="flex items-center justify-center gap-2 px-4 py-6 text-sm text-gray-500">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-primary-600" />
              Chargement…
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center px-4 py-10 text-center">
              <BellIcon className="h-8 w-8 text-gray-300" aria-hidden="true" />
              <p className="mt-2 text-sm text-gray-500">Aucune notification</p>
            </div>
          ) : (
            notifications.map((notification, index) => (
              <Link
                key={notification.id || notification._id || index}
                to={`/requests/${typeof notification.requestId === 'string'
                  ? notification.requestId
                  : notification.requestId?._id || ''}`}
                className={`block px-4 py-3 transition-colors duration-150 hover:bg-gray-50 ${
                  !notification.read ? 'bg-primary-50/70' : ''
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <div className="flex items-start gap-2.5">
                  <span
                    className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${
                      !notification.read ? 'bg-primary-500' : 'bg-transparent'
                    }`}
                    aria-hidden="true"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-sm ${!notification.read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                        {notification.message}
                      </p>
                      <p className="whitespace-nowrap text-xs text-gray-400">
                        {formatDate(notification.createdAt)}
                      </p>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Demande #{typeof notification.requestId === 'string'
                        ? notification.requestId.substring(0, 8)
                        : notification.requestId?._id?.toString().substring(0, 8) || 'N/A'}
                    </p>
                  </div>
                </div>
              </Link>
            ))
          )}
        </div>

        <div className="border-t border-gray-200 bg-gray-50 px-4 py-2.5">
          <Link
            to="/notifications"
            className="block text-center text-sm font-medium text-primary-600 transition-colors hover:text-primary-800"
            onClick={onClose}
          >
            Voir toutes les notifications
          </Link>
        </div>
      </div>
    </div>
  );
};

export default NotificationDropdown;
